import { Button } from '@/components/ui/Button';
import { showToast } from '@/components/ui/Toast';
import { useUpdateStore } from '@/stores/updateStore';

async function applyWithFeedback(apply: () => Promise<boolean>): Promise<void> {
  const ok = await apply();
  if (!ok) {
    showToast('The update is still downloading. Please try again in a moment.');
  }
}

/**
 * Server-driven update gate.
 *  - severity 'required' (running < server MinimumVersion): full-screen blocker,
 *    the driver MUST update before continuing.
 *  - severity 'optional' (running < server CurrentVersion) OR a new build is
 *    waiting in the service worker: dismissible bottom banner.
 */
export function UpdateGate() {
  const severity = useUpdateStore((s) => s.severity);
  const swWaiting = useUpdateStore((s) => s.swWaiting);
  const dismissed = useUpdateStore((s) => s.dismissed);
  const applying = useUpdateStore((s) => s.applying);
  const running = useUpdateStore((s) => s.running);
  const current = useUpdateStore((s) => s.current);
  const apply = useUpdateStore((s) => s.apply);
  const dismiss = useUpdateStore((s) => s.dismiss);

  if (severity === 'required') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-6">
        <div className="w-full max-w-sm rounded-xl bg-brand-surface p-6 text-center shadow-2xl">
          <div className="mb-3 text-4xl">⬆️</div>
          <h2 className="mb-2 text-lg font-bold text-white">Update required</h2>
          <p className="mb-1 text-sm text-brand-muted">
            A newer version of the app is required to continue.
          </p>
          <p className="mb-5 text-xs text-brand-muted">
            Installed {running}
            {current ? ` • Required ${current}` : ''}
          </p>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={applying}
            onClick={() => void applyWithFeedback(apply)}
          >
            {applying ? 'Updating…' : 'Update now'}
          </Button>
        </div>
      </div>
    );
  }

  const showOptional = (severity === 'optional' && !dismissed) || swWaiting;
  if (!showOptional) return null;

  return (
    <div className="pb-3-safe fixed inset-x-0 bottom-0 z-[90] px-3 pt-3">
      <div className="mx-auto flex max-w-md items-center gap-3 rounded-xl bg-brand-surface p-3 shadow-2xl ring-1 ring-white/10">
        <div className="flex-1">
          <p className="text-sm font-semibold text-white">Update available</p>
          <p className="text-xs text-brand-muted">
            A new version is ready{current ? ` (${current})` : ''}.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={dismiss} disabled={applying}>
          Later
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void applyWithFeedback(apply)}
          disabled={applying}
        >
          {applying ? 'Updating…' : 'Update'}
        </Button>
      </div>
    </div>
  );
}
